import { NotificationAlertErrorShieldSolidIcon } from '@tableau/icons/NotificationAlertErrorShieldSolidIcon';
import { NotificationSuccessIcon } from '@tableau/icons/NotificationSuccessIcon';
import { ButtonType, ButtonWidget, DialogType, Tooltip } from '@tableau/widgets';
import React from 'react';
import { useEffect, useState } from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { ModalInstance, ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { UpdateStringServerSettingNames } from '~src/settings/ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { SettingsStyles } from '~src/settings/styles/CommonStyles';

import { addServerExploreInTableauButton,
         exploreInTableauServerIDValidationResult,
         exploreInTableauServerTable,
         exploreInTableauValidateButton,
       } from '../../styles/IntegrationsSettingsStyles';
import { ExploreInTableauDialog, ExploreInTableauDialogProps, ExploreInTableauDialogState } from '../ExploreInTableauDialog';

export interface ExploreInTableauServerSettingsState {
  savedServer: ExploreInTableauDialogState[];
  cloudIntegrationTableauServerId: string;
  configValidationState: boolean | undefined;
}

function ExploreInTableauServerSection() {
  const [state, setState] = useState<ExploreInTableauServerSettingsState>({
    savedServer: [],
    cloudIntegrationTableauServerId: '',
    configValidationState: undefined,
  });

  useEffect(() => {
    fetchServerId(updateServerId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!FeatureFlags.instantAnalyticsOnPremMilestone3)
    return null;

  function updateServerId(serverID: string) {
    setState({...state, cloudIntegrationTableauServerId: serverID });
  }

  function updateConfigState(newConfigValidationState: boolean) {
    setState({ ...state, configValidationState: newConfigValidationState });
  }

  async function handleVerifyConfig() {
    try {
      const verificationResult = await ServerSettingsInstance.verifyCloudIntegrationApiKey(
        UpdateStringServerSettingNames.cloudIntegrationInstantAnalyticsApiKey
      );
      updateConfigState(!verificationResult.errors || verificationResult.errors.length === 0);
    } catch(error) {
      updateConfigState(false);
    }
  }

  function renderServerSection() {
    const emptyState = state.cloudIntegrationTableauServerId === '';
    return (
      <div className={SettingsStyles.indent}>
        {!emptyState && renderSavedServerTable()}
        {emptyState && getAddServerButton()}
      </div>
    );
  }

  function getAddServerButton() {
    return <ButtonWidget
      buttonType={ButtonType.Outline}
      id="add-server-ID-button"
      testId="add-server-ID-button"
      className={addServerExploreInTableauButton}
      onClick={() => openAddServerModal(updateServerId)}
      disabled={state && state.savedServer && state.savedServer.length > 0}
      type='button'>{Messages.exploreInTableauAddServerButton()}
    </ButtonWidget>;
  }

  function renderSavedServerTable() {
      const stateLoaded = state && state.savedServer;
      if (!stateLoaded) return;
      return (
        <>
          <table
            id="cloud-integration-server-id-table"
            className={`tb-static-grid-table tb-static-grid-table-settings-width ${exploreInTableauServerTable}`}
          >
            <thead>
              <tr>{renderServerListTableHeader()}</tr>
            </thead>
            <tbody id="cloud-integration-server-id-list">{renderServerListTableRow(state.cloudIntegrationTableauServerId, handleVerifyConfig)}</tbody>
          </table>

          {/* Validation message displayed below the table */}
          {displayConfigValidationMessage(state.configValidationState)}
        </>
      );
    }

  return (
    <SettingsSection
      title={Messages.exploreInTableauHeader()}
      description={Messages.exploreInTableauDescription()}
    >
      {renderServerSection()}
    </SettingsSection>);
}

function fetchServerId(setter: (serverID: string) => void) {
  ServerSettingsInstance.fetch()
    .then((settings: any) => {
      setter(settings.cloudIntegrationTableauServerId);
    })
    .catch(() => {
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.exploreInTableauFetchServerFailed(), // create this localized message for clarity
        testId: `tb-toast-fetch-server-error`
      });
    });
}

function displayConfigValidationMessage(configValidationState: boolean | undefined) {
  return (
    configValidationState !== undefined && (
      <div>
        {configValidationState ? (
          <div className={exploreInTableauServerIDValidationResult} id="server-id-validation-success-section">
            <NotificationSuccessIcon size={18} alt={Messages.exploreInTableauServerIDValidationSuccessText()} />
            <span id="server-id-validation-success-text">{ Messages.exploreInTableauServerIDValidationSuccessText() }</span>
          </div>
        ) : (
          <div className={exploreInTableauServerIDValidationResult} id="server-id-validation-fail-section">
            <NotificationAlertErrorShieldSolidIcon size={18} alt={Messages.exploreInTableauServerIDValidationFailText()}/>
            <span id="server-id-validation-fail-text">{ Messages.exploreInTableauServerIDValidationFailText() }</span>
          </div>
        )}
      </div>
    )
  );
}

function openAddServerModal(onServerAdded: (newServerId: string) => void) {
  let modalInstance: ModalInstance;
  const dialogProps: ExploreInTableauDialogProps = {
    close: (result: any) => modalInstance && modalInstance.close(result),
    onServerAdded,
  };

  modalInstance = ReactModalService.open({
    type: DialogType.NormalWithTitleBar,
    dialogContent: React.createElement(ExploreInTableauDialog, dialogProps),
    testId: 'exploreInTableauDialog',
    title: Messages.exploreInTableauAddServerDialogHeader(),
    showCloseButton: true,
    shouldNotSetDefaultWidth: true
  });

  modalInstance.result.catch((reason) => {
    if (reason === 'dismissDialog' || reason === 'cancel') {
      Promise.resolve();
    } else Promise.reject();
  });
}

function renderServerListTableRow(serverID: string, handleVerifyConfig: () => void) {
  return (
    <tr id={`server-id-row-${serverID}`} key={serverID + '-server-table-row'}>
      {/* Server ID Column */}
      <td>
        <span id={`cloud-integration-server-id-${serverID}`} className="tb-data-grid-cell-text tb-lr-padded-wide">
          {serverID}
        </span>
      </td>
      {/* Expiration date column */}
      <td>
        <span id="expiration-date-id" className="tb-data-grid-cell-text tb-lr-padded-wide">
          {
          /* TODO: @W-18564579 (https://gus.lightning.force.com/lightning/r/ADM_Work__c/a07EE00002EgYE7YAN/view) 
             Fetch expiration date from GlobalSettingState and display in table */
          }
          {'5/30/25'}
        </span>
      </td>
      {/* Validate Button Column */}
        {
          <td>
            <span className={`tb-data-grid-cell-text tb-lr-padded-wide ${exploreInTableauValidateButton}`}>
              <Tooltip tooltipContent={Messages.exploreInTableauValidateServerIDButtonTooltipText()}>
                <ButtonWidget
                  aria-label={Messages.exploreInTableauValidateServerIDButtonText()}
                  buttonType={ButtonType.Outline}
                  density='medium'
                  onClick={() => handleVerifyConfig()}
                >
                  { Messages.exploreInTableauValidateServerIDButtonText() }
                </ButtonWidget>
              </Tooltip>
            </span>
          </td>
        }

    </tr>
  );
}

function renderServerListTableHeader() {
  const tableHeaders = {
    [Messages.exploreInTableauServerIDTableHeader()]: 'server-id-header',
    [Messages.exploreInTableauExpirationDateTableHeader()]: 'expiration-date-header',
    '': ''
  };

  return Object.entries(tableHeaders).map(([label, id]) => (
    <th id={id} key={id} className="tb-data-grid-headers-line">
      <span className="tb-data-grid-header-text">{label}</span>
    </th>
  ));
}

export default ExploreInTableauServerSection;
